import { ApplicationConfig, importProvidersFrom, inject } from '@angular/core';
import { provideRouter } from '@angular/router';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideBrowserGlobalErrorListeners } from '@angular/core';
import { NZ_ICONS } from 'ng-zorro-antd/icon';
import {
  UserOutline,
  LockOutline,
  MenuUnfoldOutline,
  MenuFoldOutline,
  HomeOutline,
  LogoutOutline,
  ContactsOutline,
  EyeOutline,
  EditOutline,
  DeleteOutline,
  SearchOutline,
  CloseCircleOutline,
  CloseCircleFill,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  CaretUpOutline,
  CaretDownOutline,
  PlusOutline,
  PlusCircleOutline,
  HistoryOutline,
  ClockCircleOutline,
  SwapOutline,
  FileTextOutline,
  CloudUploadOutline,
  UploadOutline,
  FileExcelOutline,
  FilePdfOutline,
  DragOutline,
  HolderOutline,
  CloseOutline,
  MessageOutline,
  ExportOutline,
  CalendarOutline,
  CheckCircleOutline,
  CheckCircleFill,
  // Icono por defecto de <nz-popconfirm> (lo pinta con nzTheme="fill").
  // Sin registrarlo, el popconfirm sale sin icono.
  ExclamationCircleFill,
  AuditOutline,
  TeamOutline,
  LikeOutline,
  LoadingOutline,
  FolderOpenOutline,
  LeftOutline,
  RightOutline,
  FileOutline,
  DownloadOutline,
  ReloadOutline,
  SnippetsOutline,
} from '@ant-design/icons-angular/icons';
import {
  credencialesInterceptor,
  proveerSesion,
  sesionExpiradaInterceptor,
  type SesionConfig,
} from '@intelishs-group/session-core';
import { NotifyService } from '@intelishs-group/notify-service';
import { CATALOG_CONFIG } from '@intelishs-group/catalog-core';
import { COMPANIES_CONFIG } from '@intelishs-group/company-core';
import { environment } from 'src/environments/environment';
import { routes } from './app.routes';
import { proveerI18n, proveerOverlays } from '@intelishs-group/ui';

/** Lo que la sesión de plataforma necesita saber de este ambiente. */
const SESION = {
  identidadUrl: environment.identidadUrl,
  // El login CENTRAL, a donde se manda a quien no tiene sesión utilizable.
  loginUrl: environment.loginUrl,
} as const;

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    // `NzModalService` NO es `providedIn: 'root'`: lo provee `NzModalModule`. Aqui
    // nadie lo proveia — llegaba DE REBOTE del `<intelishs-file-viewer>` que
    // `app.html` monta en la raiz, porque ese componente importa el modulo en su
    // propio `imports` y eso iza el provider al injector de entorno.
    //
    // Funciona hasta que alguien quita el visor de `app.html`, y entonces se cae
    // algo que no tiene nada que ver con ese cambio. A `ffacturacion` le paso
    // exactamente eso al borrar su `modal-pdf-viewer` local: `tour-button` se
    // quedo sin el servicio que inyecta y que nunca importo el modulo.
    importProvidersFrom(NzModalModule),
    // `credencialesInterceptor` es el de la librería (session-core >= 5.2.14): pone
    // `withCredentials` en todo salvo en los `hostsSinCredenciales` de la sesión
    // (Google Calendar y el webhook de n8n, que rechazan credenciales por CORS).
    // Sustituye al `credentialsInterceptor` local que existía sólo para eso.
    //
    // `sesionExpiradaInterceptor` es el de la librería: ante un 401 refresca una
    // vez y reintenta; sólo si el refresco falla avisa y manda al login central.
    provideHttpClient(
      withInterceptors([credencialesInterceptor, sesionExpiradaInterceptor]),
    ),
    provideBrowserGlobalErrorListeners(),
    // Los overlays del CDK vuelven al apilamiento por z-index (ui >= 5.2.7). Sin
    // esto, desde Angular 21 un modal abierto tapa los avisos de SweetAlert y el
    // velo de carga: salen detras, no se pueden pulsar y la pantalla parece colgada.
    proveerOverlays(),
    // Idioma de la plataforma (ui >= 5.2.10): registra es-MX y alinea `LOCALE_ID` con el
    // `locale` de ng-zorro. Sustituye a `registerLocaleData` + `provideNzI18n` + `LOCALE_ID`.
    proveerI18n(),
    {
      provide: NZ_ICONS,
      useValue: [
        UserOutline,
        LockOutline,
        MenuUnfoldOutline,
        MenuFoldOutline,
        HomeOutline,
        LogoutOutline,
        ContactsOutline,
        EyeOutline,
        EditOutline,
        DeleteOutline,
        SearchOutline,
        CloseCircleOutline,
        CloseCircleFill,
        CheckCircleTwoTone,
        CloseCircleTwoTone,
        CaretUpOutline,
        CaretDownOutline,
        PlusOutline,
        PlusCircleOutline,
        HistoryOutline,
        ClockCircleOutline,
        SwapOutline,
        FileTextOutline,
        CloudUploadOutline,
        UploadOutline,
        FileExcelOutline,
        FilePdfOutline,
        DragOutline,
        HolderOutline,
        CloseOutline,
        MessageOutline,
        ExportOutline,
        CalendarOutline,
        CheckCircleOutline,
        CheckCircleFill,
        ExclamationCircleFill,
        AuditOutline,
        TeamOutline,
        LikeOutline,
        LoadingOutline,
        FolderOpenOutline,
        LeftOutline,
        RightOutline,
        FileOutline,
        DownloadOutline,
        ReloadOutline,
        SnippetsOutline,
      ],
    },
    // `LAYOUT_ENV` se fue con `<layout-main>`: el shell nuevo no lee nada del
    // inyector y el nombre del sistema entra por `[marca]` en `App`.
    //
    // La sesión de PLATAFORMA. Sustituye a `provideSession({ tokenName:
    // 'tk-prospectos' })`, que guardaba el JWT del SSO viejo
    // (`/Membership/GetTokenInSession`) en `sessionStorage`. Ahora la sesión la
    // resuelve identidad (`GET {identidadUrl}/sesion/actual`), llega en la cookie
    // HttpOnly `sinay_acceso` y se carga ANTES de montar el primer componente.
    proveerSesion({
      ...SESION,
      // El aviso antes de mandar al login. Es obligatorio en la libreria y no por
      // gusto: su primera version redirigia en silencio y se comio este aviso,
      // que llevaba años en produccion.
      //
      // El `inject()` aqui FUNCIONA desde 5.0.5. Antes no: esta flecha corre
      // despues del `await` del inicializador, fuera del contexto de inyeccion,
      // asi que lanzaba NG0203 y la libreria se lo tragaba —el aviso no salia y
      // nada lo delataba—. Por eso habia un `SESION_CONFIG` con `useFactory`
      // duplicando esta configuracion justo debajo. Ya no hace falta: el servicio
      // invoca el aviso con `runInInjectionContext`.
      avisarSesionCaducada: () => inject(NotifyService).unauthorizedNotify(),
      // Sin `rutaSinPermiso`: quien no tiene el modulo va a `/`, y `inicioGuard` lo lleva a SU
      // primera pantalla.
      // Terceros a los que `credencialesInterceptor` NO marca con credenciales:
      // Google Calendar (`www.googleapis.com`) y el webhook de n8n
      // (`intellishs.app.n8n.cloud`). Cada entrada cubre sus subdominios.
      hostsSinCredenciales: ['googleapis.com', 'n8n.cloud'],
    }),
    { provide: CATALOG_CONFIG, useValue: { apiUrl: environment.BaseUrl } },
    { provide: COMPANIES_CONFIG, useValue: { apiUrl: environment.BaseUrl } },
  ],
};
